import type {
  ObraStatus,
  PainelActivity,
  PainelAlerts,
  PainelAttentionRow,
  PainelMonth,
  PainelReason,
  PainelScope,
  PainelTotals,
} from '@obras/shared';
import {
  formatCurrency,
  formatCurrencyCompact,
  formatCurrencyDelta,
  formatDays,
  formatPercent,
  formatRelativeDate,
  OBRA_STATUS_LABELS,
  REPORT_TYPE_META,
} from '@obras/shared';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  CalendarClock,
  CalendarOff,
  CalendarPlus,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  CircleDot,
  CircleSlash,
  ClipboardCheck,
  Clock,
  FileText,
  HardHat,
  Minus,
  PauseCircle,
  Plus,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  UserX,
  type LucideIcon,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState, type ReactNode } from 'react';

import { Anel } from '../../components/charts/Anel.js';
import { BarList, type BarRow } from '../../components/charts/BarList.js';
import { Columns, type ColumnItem } from '../../components/charts/Columns.js';
import { NumeroQueSobe } from '../../components/charts/NumeroQueSobe.js';
import { ChartFrame, ChartTable, rotuloMes } from '../../components/charts/shared.js';
import { EASE_OUT_QUINT } from '../../components/motion.js';
import { CardHeader } from '../../components/ui/Card.js';
import { Avatar, EmptyState } from '../../components/ui/Feedback.js';
import { cn } from '../../lib/cn.js';
import { obrasSearch, type ObrasSearch } from '../obras/search.js';

const ICON = { strokeWidth: 1.5 } as const;

/** `1 obra` / `3 obras`. */
function plural(quantidade: number, singular: string, plural: string): string {
  return `${quantidade.toLocaleString('pt-BR')} ${quantidade === 1 ? singular : plural}`;
}

/** O recorte da carteira: no painel do fiscal, todo atalho leva só as obras dele. */
function daCarteira(fiscalId: string | null) {
  return fiscalId ? { fiscalIds: [fiscalId] } : {};
}

/**
 * O "nada a mostrar" de um cartão do painel.
 *
 * Uma linha, não o estado vazio de página inteira: aqui o vazio é quase sempre
 * notícia boa — nenhuma pendência, nenhuma obra parada — e não pede ação.
 */
function Quiet({
  icon: Icon,
  tone = 'text-ink-3',
  children,
}: {
  icon: LucideIcon;
  tone?: string | undefined;
  children: ReactNode;
}) {
  return (
    <p className="flex items-center gap-2.5 py-2 text-[13px] text-ink-3">
      <Icon {...ICON} size={16} className={cn('shrink-0', tone)} aria-hidden />
      <span>{children}</span>
    </p>
  );
}

/** Rótulo de bloco dentro de um cartão: o versalete do indicador. */
function Rotulo({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-2 text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
      {children}
    </h3>
  );
}

/** Atalho no cabeçalho do cartão para a tela que detalha o assunto. */
export function CardLink({
  to,
  search,
  children,
}: {
  to: '/obras' | '/relatorios' | '/tarefas';
  /** Só a lista de obras aceita recorte pela URL. */
  search?: ObrasSearch | undefined;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      search={search ?? {}}
      className="flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-primary-ink transition-colors hover:text-primary"
    >
      {children}
      <ArrowRight {...ICON} size={14} aria-hidden />
    </Link>
  );
}

// ─────────────────────────────────────────────────────── execução financeira

/**
 * Execução financeira: o pago dentro do contratado.
 *
 * O anel é o contratado inteiro, e a parte escura é o que já foi pago; no
 * miolo, a fração. Ao lado, os três valores que ele resume, na forma curta dos
 * indicadores — a dica traz o valor por extenso. As amostras ligam cada valor
 * à sua parte do anel. O saldo diz quanto dele está em obras em curso: é o que
 * ainda vai ser pago.
 */
export function ExecucaoFinanceira({ totals }: { totals: PainelTotals }) {
  const rotulo = `Pago ${formatCurrency(totals.paidCents)} de ${formatCurrency(totals.contractedCents)} contratados, ${formatPercent(totals.executionRatio)}.`;

  return (
    <>
      <CardHeader
        title="Execução financeira"
        description="Quanto do valor contratado já foi pago."
      />
      <div className="flex items-center gap-5 px-5 pt-1 pb-6 sm:gap-8">
        <Anel fracao={totals.executionRatio} rotulo={rotulo} className="size-32 sm:size-42">
          <NumeroQueSobe
            valor={totals.executionRatio}
            formato="porcentagem"
            className="numeral text-[22px] leading-none sm:text-[28px]"
          />
          <span className="mt-1.5 text-[12px] text-ink-3">pago</span>
        </Anel>

        <dl className="grid min-w-0 flex-1 gap-3 sm:grid-cols-3 sm:gap-0">
          <Figura rotulo="Contratado" valor={totals.contractedCents} />
          <Figura rotulo="Pago" valor={totals.paidCents} amostra="bg-ink" />
          <Figura
            rotulo="Saldo a executar"
            valor={totals.remainingCents}
            amostra="bg-gray-data"
            dica={`${formatCurrencyCompact(totals.aliveRemainingCents)} em obras em curso`}
          />
        </dl>
      </div>
    </>
  );
}

function Figura({
  rotulo,
  valor,
  amostra,
  dica,
}: {
  rotulo: string;
  /** Centavos. */
  valor: number;
  /** A cor da parte do anel que o valor é. */
  amostra?: string | undefined;
  dica?: string | undefined;
}) {
  return (
    <div className="min-w-0 border-l border-line pl-3 sm:pl-5">
      <dt className="flex items-center gap-1.5 text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
        {amostra ? <span aria-hidden className={cn('size-2 shrink-0', amostra)} /> : null}
        {rotulo}
      </dt>
      <dd className="numeral mt-1.5 text-[20px] leading-none sm:mt-2 sm:text-[24px]">
        <NumeroQueSobe valor={valor} formato="moeda" />
      </dd>
      {dica ? <dd className="mt-1.5 text-[12px] text-ink-3 sm:mt-2">{dica}</dd> : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── obras

/*
 * A barra é neutra; o estado vai no ícone e no rótulo. Em andamento e
 * cancelada ficam em cinza porque uma é o esperado e a outra não pede nada.
 * É a mesma escolha da faixa de resumo da listagem de obras.
 */
const STATUS_STYLE: Record<ObraStatus, { icon: LucideIcon; tone: string }> = {
  em_andamento: { icon: CircleDot, tone: 'text-ink-3' },
  paralisada: { icon: PauseCircle, tone: 'text-paused' },
  concluida: { icon: CircleCheck, tone: 'text-success' },
  cancelada: { icon: CircleSlash, tone: 'text-ink-3' },
};

/**
 * As obras, quantas e em que situação. Cada situação leva à lista de obras já
 * recortada nela — com a carteira, no painel do fiscal. Cancelada só aparece
 * quando há alguma.
 */
export function ObrasPorSituacao({
  totals,
  scope,
  fiscalId,
}: {
  totals: PainelTotals;
  scope: PainelScope;
  fiscalId: string | null;
}) {
  const carteira = daCarteira(fiscalId);
  const statuses = (Object.keys(STATUS_STYLE) as ObraStatus[]).filter(
    (status) => status !== 'cancelada' || totals.byStatus[status] > 0,
  );

  const rows: BarRow[] = statuses.map((status) => ({
    key: status,
    label: OBRA_STATUS_LABELS[status],
    icon: STATUS_STYLE[status].icon,
    iconTone: STATUS_STYLE[status].tone,
    value: totals.byStatus[status],
    display: String(totals.byStatus[status]),
    link: { to: '/obras', search: obrasSearch({ statuses: [status], ...carteira }) },
  }));

  const ondeEstao =
    scope === 'carteira'
      ? totals.obras === 1
        ? 'obra sob sua fiscalização'
        : 'obras sob sua fiscalização'
      : totals.obras === 1
        ? 'obra no acervo'
        : 'obras no acervo';

  return (
    <>
      <CardHeader
        title="Obras"
        action={
          <CardLink to="/obras" search={fiscalId ? obrasSearch(carteira) : undefined}>
            Ver obras
          </CardLink>
        }
      />
      <div className="px-5 pb-6">
        <p className="flex items-baseline gap-2">
          <NumeroQueSobe
            valor={totals.obras}
            formato="inteiro"
            className="numeral text-[32px] leading-none"
          />
          <span className="text-[13px] text-ink-3">{ondeEstao}</span>
        </p>
        <div className="mt-5">
          <BarList rows={rows} max={totals.obras} labelWidth="sm" />
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────── atenção

/**
 * Os sete motivos, sempre todos — inclusive zerados.
 *
 * Sumir com "paralisadas" quando chega a zero tiraria justamente a informação
 * boa. O zero fica em cinza; acima de zero, o ícone e o número ganham a cor do
 * estado. Cada motivo leva à lista de obras com o recorte que conta
 * exatamente o que ele diz.
 */
const MOTIVOS: readonly {
  readonly key: keyof PainelAlerts;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly tone: string;
  readonly recorte: Parameters<typeof obrasSearch>[0];
}[] = [
  {
    key: 'overdueVisits',
    label: 'Visitas vencidas',
    icon: TriangleAlert,
    tone: 'text-danger',
    recorte: { pendencias: ['visita_vencida'] },
  },
  {
    key: 'dueSoonVisits',
    label: 'Visitas vencendo',
    icon: Clock,
    tone: 'text-warning',
    recorte: { pendencias: ['visita_vencendo'] },
  },
  {
    key: 'withoutVisit',
    label: 'Sem visita',
    icon: CalendarOff,
    tone: 'text-warning',
    recorte: { pendencias: ['sem_visita'] },
  },
  {
    key: 'paused',
    label: 'Paralisadas',
    icon: PauseCircle,
    tone: 'text-paused',
    recorte: { statuses: ['paralisada'] },
  },
  {
    key: 'expiredContracts',
    label: 'Contratos vencidos',
    icon: CalendarClock,
    tone: 'text-danger',
    recorte: { pendencias: ['contrato_vencido'] },
  },
  {
    key: 'behindSchedule',
    label: 'Execução atrasada',
    icon: TrendingDown,
    tone: 'text-warning',
    recorte: { pendencias: ['atrasada'] },
  },
  {
    key: 'withoutFiscal',
    label: 'Sem fiscal',
    icon: UserX,
    tone: 'text-warning',
    recorte: { pendencias: ['sem_fiscal'] },
  },
];

/**
 * O motivo é texto com um ícone na cor do estado, não uma pílula colorida.
 *
 * Três pílulas por obra, vezes oito obras, era uma parede de cor: o olho não
 * sabia por onde começar. Em texto corrido, a cor fica só no ícone, e a linha
 * se lê como frase — "visita vencida há 33 dias · contrato vencido há 12 dias".
 */
const REASON_STYLE: Record<PainelReason, { tone: string; icon: LucideIcon }> = {
  visita_vencida: { tone: 'text-danger', icon: TriangleAlert },
  paralisada: { tone: 'text-paused', icon: PauseCircle },
  contrato_vencido: { tone: 'text-danger', icon: CalendarClock },
  atrasada: { tone: 'text-warning', icon: TrendingDown },
  sem_fiscal: { tone: 'text-warning', icon: UserX },
  sem_visita: { tone: 'text-ink-3', icon: CalendarOff },
  visita_vencendo: { tone: 'text-warning', icon: Clock },
};

/**
 * O motivo com o número que o torna urgente.
 *
 * "Há 12 dias" e "pago 48% com 88% do prazo" — nunca uma diferença em pontos
 * percentuais: "atraso de 40 p.p." obrigava a saber o que é um ponto
 * percentual para entender que a obra pagou menos da metade com o prazo quase
 * no fim.
 */
function reasonLabel(row: PainelAttentionRow, reason: PainelReason): string {
  switch (reason) {
    case 'visita_vencida':
      return row.visitOverdueDays
        ? `Visita vencida há ${formatDays(row.visitOverdueDays)}`
        : 'Visita vencida';
    case 'paralisada':
      return row.pausedDays ? `Paralisada há ${formatDays(row.pausedDays)}` : 'Paralisada';
    case 'contrato_vencido':
      return row.contractExpiredDays
        ? `Contrato vencido há ${formatDays(row.contractExpiredDays)}`
        : 'Contrato vencido';
    case 'atrasada':
      return `Pago ${formatPercent(row.executionProgress, 0)} com ${formatPercent(row.timeProgress, 0)} do prazo`;
    case 'sem_fiscal':
      return 'Sem fiscal';
    case 'sem_visita':
      return 'Sem visita';
    case 'visita_vencendo':
      return 'Visita vencendo';
  }
}

/** Quantas obras a lista mostra antes do "mostrar mais". */
const PRIMEIRAS = 5;

/**
 * O que pede ação hoje, em duas partes: à esquerda, **quantas por motivo**; à
 * direita, **as obras**, da mais urgente para a menos, cada uma com o porquê
 * escrito e, no acervo, o fiscal responsável. Mostra as cinco primeiras; as
 * outras abrem no lugar.
 */
export function PrecisaDeAtencao({
  alerts,
  attention,
  scope,
  fiscalId,
}: {
  alerts: PainelAlerts;
  attention: readonly PainelAttentionRow[];
  scope: PainelScope;
  fiscalId: string | null;
}) {
  const [todas, setTodas] = useState(false);
  const carteira = daCarteira(fiscalId);
  const primeiras = attention.slice(0, PRIMEIRAS);
  const resto = attention.slice(PRIMEIRAS);
  const comFiscal = scope === 'acervo';

  return (
    <>
      <CardHeader
        title="Precisa de atenção"
        description={
          scope === 'carteira'
            ? 'Obras sob sua fiscalização que pedem uma decisão hoje.'
            : 'Obras em curso que pedem uma decisão hoje.'
        }
      />

      <div className="grid grid-cols-1 gap-6 px-5 pb-5 md:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] md:gap-0">
        <section aria-label="Pendências por motivo" className="md:pr-6">
          <Rotulo>Por motivo</Rotulo>
          <ul className="grid grid-cols-2 gap-x-5 md:grid-cols-1">
            {MOTIVOS.map((motivo) => {
              const valor = alerts[motivo.key];
              const ativo = valor > 0;

              return (
                <li key={motivo.key}>
                  <Link
                    to="/obras"
                    search={obrasSearch({ ...motivo.recorte, ...carteira })}
                    title="Ver na lista de obras"
                    className="-mx-2 flex items-center gap-2.5 px-2 py-1.5 text-[13px] transition-colors duration-100 hover:bg-sunken"
                  >
                    <motivo.icon
                      size={15}
                      strokeWidth={1.75}
                      className={cn('shrink-0', ativo ? motivo.tone : 'text-ink-3/50')}
                      aria-hidden
                    />
                    <span className={cn('min-w-0 flex-1 truncate', !ativo && 'text-ink-3')}>
                      {motivo.label}
                    </span>
                    <span className={cn('tabular font-medium', ativo ? motivo.tone : 'text-ink-3')}>
                      {valor}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>

        <section
          aria-label="Obras que pedem atenção"
          className="md:border-l md:border-line md:pl-6"
        >
          <Rotulo>
            {attention.length > 0
              ? `${plural(attention.length, 'obra', 'obras')}, da mais urgente`
              : 'Obras'}
          </Rotulo>
          {attention.length === 0 ? (
            <Quiet icon={CircleCheck} tone="text-success">
              Nenhuma obra pede atenção hoje.
            </Quiet>
          ) : (
            <>
              <ul>
                {primeiras.map((row) => (
                  <li key={row.id}>
                    <ObraLinha row={row} comFiscal={comFiscal} />
                  </li>
                ))}
                <AnimatePresence initial={false}>
                  {todas
                    ? resto.map((row) => (
                        <motion.li
                          key={row.id}
                          className="overflow-hidden"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2, ease: EASE_OUT_QUINT }}
                        >
                          <ObraLinha row={row} comFiscal={comFiscal} />
                        </motion.li>
                      ))
                    : null}
                </AnimatePresence>
              </ul>

              {resto.length > 0 ? (
                <button
                  type="button"
                  aria-expanded={todas}
                  onClick={() => setTodas((atual) => !atual)}
                  className="mt-2 flex items-center gap-1.5 text-[13px] font-medium text-primary-ink transition-colors hover:text-primary"
                >
                  {todas
                    ? 'Mostrar menos'
                    : `Mostrar mais ${plural(resto.length, 'obra', 'obras')}`}
                  <ChevronDown
                    {...ICON}
                    size={14}
                    className={cn('transition-transform duration-200', todas && 'rotate-180')}
                    aria-hidden
                  />
                </button>
              ) : null}
            </>
          )}
        </section>
      </div>
    </>
  );
}

/**
 * A foto da obra, em miniatura, antes do nome.
 *
 * É a mesma capa do cartão de obras, para a pessoa reconhecer a obra de
 * relance, sem ler. Sem foto, o quadro fica no cinza da superfície com o
 * ícone da obra — nunca vazio, senão a lista fica com buracos.
 */
function Miniatura({ url }: { url: string | null }) {
  return (
    <span className="grid h-9 w-12 shrink-0 place-items-center overflow-hidden bg-sunken">
      {url ? (
        <img src={url} alt="" loading="lazy" className="size-full object-cover" />
      ) : (
        <HardHat {...ICON} size={15} className="text-ink-3" aria-hidden />
      )}
    </span>
  );
}

function ObraLinha({ row, comFiscal }: { row: PainelAttentionRow; comFiscal: boolean }) {
  return (
    <Link
      to="/obras/$obraId"
      params={{ obraId: row.id }}
      className="-mx-2 flex items-center gap-3 px-2 py-2 transition-colors duration-100 hover:bg-sunken"
    >
      <Miniatura url={row.coverUrl} />

      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-[13px] font-medium">{row.name}</span>
          <span className="shrink-0 font-mono text-[11px] text-ink-3">
            {row.contractNumber ?? 'sem contrato'}
          </span>
        </span>

        <span className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-ink-3">
          {row.reasons.map((reason) => {
            const style = REASON_STYLE[reason];
            return (
              <span key={reason} className="flex items-center gap-1">
                <style.icon size={13} strokeWidth={1.75} className={style.tone} aria-hidden />
                {reasonLabel(row, reason)}
              </span>
            );
          })}
        </span>
      </span>

      {/* Quem responde pela obra; a obra sem fiscal já diz isso nos motivos. */}
      {comFiscal ? (
        <span className="hidden w-40 shrink-0 items-center gap-2 text-[12px] text-ink-3 lg:flex">
          {row.fiscalName ? (
            <>
              <Avatar name={row.fiscalName} size={20} />
              <span className="truncate">{row.fiscalName}</span>
            </>
          ) : null}
        </span>
      ) : null}
    </Link>
  );
}

// ─────────────────────────────────────────────────────── medições por mês

export function MedicoesPorMes({
  months,
  today,
}: {
  months: readonly PainelMonth[];
  today: string;
}) {
  const atual = today.slice(0, 7);
  const total = months.reduce((soma, mes) => soma + mes.measuredCents, 0);
  const relatorios = months.reduce((soma, mes) => soma + mes.reports, 0);

  const pico = months.reduce<PainelMonth | null>(
    (maior, mes) => (mes.measuredCents > (maior?.measuredCents ?? 0) ? mes : maior),
    null,
  );

  const items: ColumnItem[] = months.map((mes) => {
    const emCurso = mes.month === atual;
    const rotulo = rotuloMes(mes.month);

    return {
      key: mes.month,
      label: rotulo,
      value: mes.measuredCents,
      tone: emCurso ? 'soft' : 'neutral',
      tip: {
        title: emCurso ? `${rotulo} · em curso` : rotulo,
        rows: [
          { label: 'Medido', value: formatCurrency(mes.measuredCents) },
          { label: 'Relatórios', value: String(mes.reports) },
          { label: 'Visitas', value: String(mes.visits) },
        ],
      },
      aria: `${rotulo}: ${formatCurrency(mes.measuredCents)} medidos, ${plural(mes.reports, 'relatório', 'relatórios')}`,
    };
  });

  const summary =
    total > 0 ? (
      <>
        {formatCurrency(total)} em doze meses · maior mês:{' '}
        {pico ? `${rotuloMes(pico.month)}, ${formatCurrencyCompact(pico.measuredCents)}` : '—'}
      </>
    ) : (
      <>{plural(relatorios, 'relatório publicado', 'relatórios publicados')} em doze meses</>
    );

  return (
    <ChartFrame
      summary={summary}
      table={
        <ChartTable
          head={['Mês', 'Medido', 'Relatórios', 'Visitas']}
          rows={months.map((mes) => [
            rotuloMes(mes.month),
            formatCurrency(mes.measuredCents),
            String(mes.reports),
            String(mes.visits),
          ])}
        />
      }
    >
      {total > 0 ? (
        <Columns items={items} format={formatCurrencyCompact} height={240} />
      ) : (
        <Quiet icon={TrendingUp}>Nenhuma medição nos últimos doze meses.</Quiet>
      )}
    </ChartFrame>
  );
}

// ─────────────────────────────────────────────────────── atividade recente

/** Quantos relatórios a atividade mostra: o bastante para a coluna, sem rolar. */
const ATIVIDADES = 6;

/** Ícones canônicos das movimentações — design.md §7. */
const TYPE_ICON: Record<string, LucideIcon> = {
  ClipboardCheck,
  TrendingUp,
  Plus,
  Minus,
  CalendarPlus,
  CircleAlert,
};

/** A cor da movimentação é lei: a mesma na timeline, no badge e aqui — design.md §4. */
const TONE_TEXT: Record<string, string> = {
  muted: 'text-ink-3',
  success: 'text-success',
  info: 'text-info',
  danger: 'text-danger',
  warning: 'text-warning',
};

/** O que a movimentação fez com o contrato, em uma palavra numérica. */
function deltaOf(item: PainelActivity): string | null {
  const meta = REPORT_TYPE_META[item.type];

  if (item.days !== null && (meta.effect === 'contract_term' || meta.effect === 'os_term')) {
    return `+${formatDays(item.days)}`;
  }
  if (item.amountCents === null || meta.effect === 'none') return null;
  if (meta.effect === 'gloss') return formatCurrency(item.amountCents);

  return formatCurrencyDelta(item.amountCents);
}

/**
 * Os últimos relatórios publicados, um por linha: o tipo no ícone, na cor da
 * movimentação; o título e o efeito no contrato; a obra, quem publicou e
 * quando. O lançamento estornado depois aparece riscado.
 */
export function AtividadeRecente({ rows }: { rows: readonly PainelActivity[] }) {
  if (rows.length === 0) {
    return (
      <div className="px-5 pb-5">
        <Quiet icon={FileText}>Nenhum relatório publicado até agora.</Quiet>
      </div>
    );
  }

  return (
    <ul className="px-5 pb-4">
      {rows.slice(0, ATIVIDADES).map((item) => {
        const meta = REPORT_TYPE_META[item.type];
        const Icon = TYPE_ICON[meta.icon] ?? FileText;
        const delta = deltaOf(item);
        const tom = TONE_TEXT[meta.tone] ?? 'text-ink-3';

        return (
          <li key={item.id}>
            <Link
              to="/obras/$obraId"
              params={{ obraId: item.obraId }}
              className="-mx-2 flex items-center gap-3 px-2 py-2 transition-colors duration-100 hover:bg-sunken"
            >
              <span className="grid size-8 shrink-0 place-items-center bg-sunken">
                <Icon {...ICON} size={15} className={tom} aria-hidden />
              </span>

              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-[13px] font-medium">{item.title}</span>
                  {delta ? (
                    <span
                      className={cn(
                        'tabular shrink-0 text-[12px] font-medium',
                        item.reversed ? 'text-ink-3 line-through' : tom,
                      )}
                      title={item.reversed ? `${meta.label} · estornada` : meta.label}
                    >
                      {delta}
                    </span>
                  ) : null}
                </span>
                <span className="mt-0.5 flex items-baseline justify-between gap-3 text-[12px] text-ink-3">
                  <span className="truncate">
                    {item.obraName} · {item.authorName ?? 'autor removido'}
                  </span>
                  <span className="shrink-0">{formatRelativeDate(item.visitDate)}</span>
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** Estado vazio de página inteira: não há obra para ler. */
export function NoObras({ scope }: { scope: PainelScope }) {
  return (
    <EmptyState
      icon={HardHat}
      title={scope === 'carteira' ? 'Nenhuma obra sob sua fiscalização' : 'Nenhuma obra cadastrada'}
      description={
        scope === 'carteira'
          ? 'O painel passa a mostrar prazos, visitas e medições quando uma obra for designada a você.'
          : 'Cadastre a primeira obra para o painel começar a acompanhar prazos, visitas e medições.'
      }
      action={<CardLink to="/obras">Ir para obras</CardLink>}
    />
  );
}
